/**
 * Transaction Cancellation Request Screen
 *
 * Screen for initiating a transaction cancellation with reason selection
 * Shows transaction details and allows user to select cancellation reason
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TokenUSDC } from "@web3icons/react";
import { ArrowLeft, ArrowRightLeft, Check, Receipt, X } from "lucide-react";
import { useState } from "react";
import type { RefundCommitRequest, TransactionStatusResponse } from "@/api";
import { CANCELLATION_REASONS } from "@/constants/cancellation-reasons";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { useRampsStore } from "@/stores/use-ramps-store";

import { RampsButton } from "../components/ramps/Button";
import { RampsDropdown } from "../components/ramps/Select";
import { RampsText } from "../components/ramps/Text";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTitle,
} from "../components/ui/drawer";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { navigateWithRampsState } from "../lib/ramps-navigation";

// Helper function to format reference number
const getFormattedReferenceNumber = (referenceNumber?: string) => {
	if (!referenceNumber) return "";
	// Format as XXX XXX XXX for readability
	return referenceNumber
		.slice(0, 9)
		.replace(/(.{3})/g, "$1 ")
		.trim();
};

// Helper function to get display text for transaction type
const getTransactionTypeDisplay = (kind?: string) => {
	if (!kind) return t`Transaction`;
	const upperKind = kind.toUpperCase();
	switch (upperKind) {
		case "WITHDRAWAL":
			return t`Cash out`;
		case "DEPOSIT":
			return t`Cash In`;
		default:
			return kind;
	}
};

export const Route = createFileRoute("/ramps/cancel")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
			refNumber?: string;
			lang?: string;
			wallet_provider?: string;
		},
	component: CancellationRequestPage,
});

function CancellationRequestPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const store = useRampsStore();

	// Get auth token (supports both v3 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search);

	// Get transaction ID from URL or store (v3 may have it from prepare endpoint)
	const transactionId = search.transaction_id || store.transactionId;

	const { apiClient } = useApiClientWithAutoVersion({
		authToken: authToken || "",
	});
	const [selectedReason, setSelectedReason] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showReasonDrawer, setShowReasonDrawer] = useState(false);

	// Fetch transaction data if coming from transaction-status page
	// For v3: refNumber is not in URL params, API doesn't require it
	// For v5: refNumber may be passed in URL params
	const { data: transactionData, isLoading: isLoadingTransaction } =
		useQuery<TransactionStatusResponse | null>({
			queryKey: ["transaction-data", transactionId, search.refNumber],
			queryFn: async () => {
				if (!transactionId || !authToken) return null;
				return await apiClient.transaction.getTransactionStatus(
					transactionId,
					search.refNumber, // Optional - v3 doesn't require it
					authToken,
				);
			},
			enabled: !!transactionId && !!authToken,
		});

	// Fetch refund info when reason is selected (for future use)
	const { data: refundInfo, isFetching: refundInfoIsFetching } = useQuery({
		queryKey: ["refund-info", transactionId, selectedReason],
		queryFn: async () => {
			if (!transactionId || !authToken || !selectedReason) return null;
			return await apiClient.transaction.getRefundInfo(
				transactionId,
				selectedReason,
				authToken,
			);
		},
		enabled: !!transactionId && !!authToken && !!selectedReason,
	});

	const handleBack = () => {
		// Build search params dynamically to include both v3 and v5 token params
		navigate({
			to: "/transaction-status",
			search: {
				transaction_id: transactionId || "",
				refNumber: search.refNumber,
				wallet_provider: store.walletProvider,
				lang: store.lang,
				token: search.token,
				accesstoken: search.accesstoken,
			},
		});
	};

	const handleReasonSelect = (reason: string) => {
		setSelectedReason(reason);
		setShowReasonDrawer(false);
	};

	const handleCancelTransaction = async () => {
		if (!selectedReason || !transactionId || !authToken) return;

		setIsSubmitting(true);

		try {
			// Prepare refund commit request
			const refundRequest: RefundCommitRequest = {
				userLanguage: store.lang || "en-US",
				targetAudience: "CONSUMER_FACING",
				refundReasonCode:
					selectedReason as RefundCommitRequest["refundReasonCode"],
				sendAmount: {
					currencyCode:
						transactionData?.sendAmountDetails?.amount?.currencyCode || "USDC",
					value: transactionData?.sendAmountDetails?.amount?.value || 0,
				},
			};

			// Commit the refund
			await apiClient.transaction.commitRefund(
				transactionId,
				refundRequest,
				authToken,
			);

			// Navigate to success screen
			navigate(
				navigateWithRampsState(search, {
					to: "/ramps/cancel-success",
				}),
			);
		} catch (_error) {
			// Cancellation failed
			// Show error message to user
		} finally {
			setIsSubmitting(false);
		}
	};

	const selectedReasonLabel = CANCELLATION_REASONS.find(
		(reason) => reason.value === selectedReason,
	)?.description;

	return (
		<div
			className="min-h-screen flex flex-col"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label="Back"
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8">
				<Progress
					value={20}
					className="h-1 bg-ramps-background-surface-2 [&>*]:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 flex flex-col">
				<div className="text-left mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Request to cancel transfer`}
					</RampsText>
				</div>

				{/* Transaction Details */}
				<div className="space-y-6 mb-8">
					{/* Reference Number */}
					<div className="flex items-center gap-4">
						<div className="p-3 ">
							<Receipt size={20} />
						</div>
						<div>
							<RampsText variant="body-sm" color="secondary" className="mb-1">
								{t`Reference #`}
							</RampsText>
							{transactionData?.referenceNumber ? (
								<RampsText variant="body-default">
									{getFormattedReferenceNumber(transactionData.referenceNumber)}
								</RampsText>
							) : (
								<Skeleton className="h-6 w-24" />
							)}
						</div>
					</div>

					{/* Transfer Amount */}
					<div className="flex items-center gap-4">
						<TokenUSDC className="w-7 h-7 ml-3" />
						<div>
							<RampsText variant="body-sm" color="secondary" className="mb-1">
								{t`Transfer amount`}
							</RampsText>
							{refundInfo?.totalRefundAmount ||
							transactionData?.sendAmountDetails?.totalAmount ? (
								<RampsText variant="body-default">
									{refundInfo?.totalRefundAmount
										? `${refundInfo.totalRefundAmount.value.toFixed(2)} ${refundInfo.totalRefundAmount.currencyCode}`
										: `${transactionData?.sendAmountDetails?.totalAmount?.value.toFixed(2)} ${transactionData?.sendAmountDetails?.totalAmount?.currencyCode}`}
								</RampsText>
							) : (
								<Skeleton className="h-6 w-32" />
							)}
						</div>
					</div>

					{/* Type */}
					<div className="flex items-center gap-4">
						<div className="p-3 rounded-lg">
							<ArrowRightLeft
								size={20}
								style={{ color: "var(--ramps-text)" }}
							/>
						</div>
						<div>
							<RampsText variant="body-sm" color="secondary" className="mb-1">
								{t`Type`}
							</RampsText>
							{transactionData?.kind ? (
								<RampsText variant="body-default">
									{getTransactionTypeDisplay(transactionData.kind)}
								</RampsText>
							) : (
								<Skeleton className="h-6 w-20" />
							)}
						</div>
					</div>
				</div>

				{/* Reason Selection */}
				<div className="mb-8">
					<RampsDropdown
						label={selectedReasonLabel || "Select reason"}
						onClick={() => setShowReasonDrawer(true)}
					/>
				</div>
			</div>

			{/* Bottom Button */}
			<div className="p-4">
				<RampsButton
					variant="primary"
					size="large"
					className="w-full"
					onClick={handleCancelTransaction}
					disabled={
						!selectedReason ||
						isSubmitting ||
						!transactionData ||
						isLoadingTransaction ||
						refundInfoIsFetching
					}
					loading={isSubmitting || refundInfoIsFetching}
				>
					{t`Cancel transaction`}
				</RampsButton>
			</div>

			{/* Reason Selection Drawer */}
			<Drawer open={showReasonDrawer} onOpenChange={setShowReasonDrawer}>
				<DrawerContent className="bg-white rounded-t-2xl max-w-md mx-auto shadow-2xl">
					<DrawerTitle className="sr-only">{t`Select Cancellation Reason`}</DrawerTitle>
					<div className="px-6 pb-8 pt-2">
						{/* Close button */}
						<DrawerClose asChild>
							<button
								type="button"
								className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
							>
								<X className="w-5 h-5 text-gray-600" />
							</button>
						</DrawerClose>

						{/* Header */}
						<div className="text mt-4">
							<RampsText
								variant="title-lg-bold"
								color="primary"
								className="mb-2"
							>
								{t`Select reason`}
							</RampsText>
						</div>

						{/* Reason Options */}
						<div className="space-y-1">
							{CANCELLATION_REASONS.map((reason) => (
								<button
									key={reason.value}
									type="button"
									onClick={() => handleReasonSelect(reason.value)}
									className="w-full flex items-center justify-between p-4 hover:bg-ramps-surface-2 transition-colors"
								>
									<RampsText variant="body-default" color="primary">
										{reason.description}
									</RampsText>
									{selectedReason === reason.value && (
										<div
											className="w-5 h-5 rounded-full flex items-center justify-center"
											style={{ backgroundColor: "var(--ramps-primary)" }}
										>
											<Check size={12} style={{ color: "white" }} />
										</div>
									)}
								</button>
							))}
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}
